import {
  DEFAULT_TRAINING_LANGUAGE,
  TRAINING_LANGUAGES,
  availableTrainingLanguages,
  resolveTrainingText,
  type TrainingContentItemDetail,
  type TrainingLanguage,
  type TrainingTextTranslations,
} from '@hrms/shared';

/**
 * Which language a learner sees a course in (ADR 0039, PLAN §5.4), as pure functions so the player,
 * the switcher, the outline and the cards agree and are tested on their own.
 *
 * A lesson's own columns are its primary version, in `language`; `variants` are the same video in
 * other languages and `translations` the title / description in them. The learner picks ONE language
 * for the course; a lesson that has nothing in that language simply shows its own text and video.
 * Nothing here ever changes what a lesson without languages looks like.
 */

/** The learner's last choice, per browser (ADR 0039: a stored per-person preference is later work). */
export const TRAINING_LANGUAGE_STORAGE_KEY = 'hrms.training.language';

type LanguageStorage = Pick<Storage, 'getItem' | 'setItem'>;

/** `localStorage`, or `null` where reading it throws (private windows, blocked site data). */
function browserStorage(): LanguageStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function isTrainingLanguage(value: unknown): value is TrainingLanguage {
  return typeof value === 'string' && (TRAINING_LANGUAGES as readonly string[]).includes(value);
}

/** The remembered choice; `null` when there is none, it is not a known language, or storage throws. */
export function readRememberedTrainingLanguage(
  storage: LanguageStorage | null = browserStorage(),
): TrainingLanguage | null {
  if (!storage) return null;
  try {
    const value = storage.getItem(TRAINING_LANGUAGE_STORAGE_KEY);
    return isTrainingLanguage(value) ? value : null;
  } catch {
    return null;
  }
}

/** Best effort: `false` when storage is missing or throws — the choice then lasts for this page only. */
export function rememberTrainingLanguage(
  language: TrainingLanguage,
  storage: LanguageStorage | null = browserStorage(),
): boolean {
  if (!storage) return false;
  try {
    storage.setItem(TRAINING_LANGUAGE_STORAGE_KEY, language);
    return true;
  } catch {
    return false;
  }
}

interface LanguageSource {
  readonly language?: TrainingLanguage | undefined;
  readonly translations?: TrainingTextTranslations | undefined;
  readonly variants?: ReadonlyArray<{ readonly language: TrainingLanguage }> | undefined;
}

/** The language a course's or lesson's own text and video are in. */
export function ownLanguage(source: { readonly language?: TrainingLanguage | undefined }): TrainingLanguage {
  return source.language ?? DEFAULT_TRAINING_LANGUAGE;
}

/** The language a lesson is shown in: the learner's choice when the lesson has it, else its own. */
export function lessonLanguage(lesson: LanguageSource, chosen: TrainingLanguage | null): TrainingLanguage {
  if (chosen && availableTrainingLanguages(lesson).includes(chosen)) return chosen;
  return ownLanguage(lesson);
}

/** Every language anything in the course is in, in the fixed order — the switcher's options. One
 * entry means there is nothing to switch, and the switcher stays hidden. */
export function courseLanguages(task: LanguageSource, items: readonly LanguageSource[]): TrainingLanguage[] {
  const present = new Set<TrainingLanguage>();
  for (const source of [task, ...items]) {
    for (const language of availableTrainingLanguages(source)) present.add(language);
  }
  return TRAINING_LANGUAGES.filter((language) => present.has(language));
}

/** What the switcher shows as selected: the choice when the course has it, else `fallback` (the open
 * lesson's or the course's own language). */
export function switcherLanguage(
  languages: readonly TrainingLanguage[],
  chosen: TrainingLanguage | null,
  fallback: TrainingLanguage,
): TrainingLanguage {
  return chosen && languages.includes(chosen) ? chosen : fallback;
}

// ---- Text ------------------------------------------------------------------------------------------

/** A course or lesson with its title and description in `chosen` (per field, falling back to its own
 * text). Returns the same object when nothing changes, so a course without languages is untouched. */
export function localizeText<
  T extends {
    title: string;
    description: string | null;
    language?: TrainingLanguage | undefined;
    translations?: TrainingTextTranslations | undefined;
  },
>(source: T, chosen: TrainingLanguage | null): T {
  if (!chosen) return source;
  const text = resolveTrainingText(source, source.translations, chosen);
  if (text.title === source.title && text.description === source.description) return source;
  return { ...source, title: text.title, description: text.description };
}

// ---- Video versions --------------------------------------------------------------------------------

export interface LessonVersion {
  /** The language of the video actually played (the lesson's own when it has no video in the choice). */
  language: TrainingLanguage;
  /** One of the lesson's `variants`, not its own video — only then do heartbeats name the language. */
  variant: boolean;
  /** The lesson as the video players read it: url, kind, length, embed and coverage of THIS version. */
  item: TrainingContentItemDetail;
}

/**
 * The video version of `lesson` to play in `language`. A lesson without variants comes back exactly
 * as it is. Coverage (`percent`, which caps seeking in strict playback) is the version's own from
 * `percentByLanguage` — the row's `percent` is the best of all languages, so watching the Hindi video
 * to the end must not let the English scrubber jump to the end. Heartbeat responses carry the map too
 * (`applyHeartbeatResult`), so it moves during a visit the way the row's `percent` does.
 */
export function lessonVersion(lesson: TrainingContentItemDetail, language: TrainingLanguage): LessonVersion {
  const own = ownLanguage(lesson);
  if (!lesson.variants || lesson.variants.length === 0) return { language: own, variant: false, item: lesson };
  const byLanguage = lesson.percentByLanguage;
  const variant = language === own ? undefined : lesson.variants.find((v) => v.language === language);
  if (!variant) {
    // No map → only the lesson's own video has ever been watched, so the row's percent is its own.
    const percent = byLanguage ? (byLanguage[own] ?? 0) : lesson.percent;
    return { language: own, variant: false, item: percent === lesson.percent ? lesson : { ...lesson, percent } };
  }
  return {
    language: variant.language,
    variant: true,
    item: {
      ...lesson,
      kind: variant.kind,
      url: variant.url,
      mimeType: variant.mimeType,
      durationSeconds: variant.durationSeconds,
      embedProvider: variant.embedProvider,
      embedId: variant.embedId,
      percent: byLanguage?.[variant.language] ?? 0,
      lastPositionSeconds: lesson.lastPositionByLanguage?.[variant.language] ?? null,
    },
  };
}

/**
 * `version.item` with its coverage lifted to `reachedSeconds`, a point of THIS version the learner played
 * to during this visit. Heartbeats keep `percentByLanguage` current, but the beat carrying the last
 * stretch may still be in flight when the version mounts again (back to a lesson, or a language switch
 * and back), and the strict seek-lock would then clamp the resume jump to the older coverage. A lesson
 * without language versions comes back exactly as it is.
 */
export function withReachedPosition(
  version: LessonVersion,
  reachedSeconds: number | undefined,
): TrainingContentItemDetail {
  const item = version.item;
  if (!item.variants || item.variants.length === 0) return item;
  const duration = item.durationSeconds;
  if (reachedSeconds === undefined || !Number.isFinite(reachedSeconds) || reachedSeconds <= 0) return item;
  if (!duration || duration <= 0) return item;
  const percent = Math.min(100, (reachedSeconds / duration) * 100);
  return percent > item.percent ? { ...item, percent } : item;
}

/** Where the saved playhead of one version is: the lesson's own in `lastPositionSeconds`, the others
 * in `lastPositionByLanguage`. `null` = never played in that language → start at the beginning. */
export function savedPosition(lesson: TrainingContentItemDetail, language: TrainingLanguage): number | null {
  return lessonVersion(lesson, language).item.lastPositionSeconds;
}

/**
 * The same moment in a version of another length: halfway through the 10-minute English video is
 * halfway through the 12-minute Hindi one. Without both lengths, the same second (within the target).
 */
export function proportionalPosition(
  seconds: number,
  fromDurationSeconds: number | null,
  toDurationSeconds: number | null,
): number {
  if (!Number.isFinite(seconds) || seconds <= 0) return 0;
  const to = toDurationSeconds && toDurationSeconds > 0 ? toDurationSeconds : null;
  const from = fromDurationSeconds && fromDurationSeconds > 0 ? fromDurationSeconds : null;
  if (to === null) return seconds;
  if (from === null) return Math.min(seconds, to);
  return Math.min(to, (seconds / from) * to);
}

/** A playhead and the version it belongs to — where a video was left during this visit. */
export interface VersionPosition {
  seconds: number;
  language: TrainingLanguage;
}

/**
 * Where a lesson's video opens in `language` (a version language from `lessonVersion`): where it was
 * left this visit — carried over proportionally when that was another version — else that version's
 * saved position.
 */
export function resumeSeconds(
  lesson: TrainingContentItemDetail,
  language: TrainingLanguage,
  leftHere: VersionPosition | undefined,
): number | null {
  if (!leftHere) return savedPosition(lesson, language);
  if (leftHere.language === language) return leftHere.seconds;
  return proportionalPosition(
    leftHere.seconds,
    lessonVersion(lesson, leftHere.language).item.durationSeconds,
    lessonVersion(lesson, language).item.durationSeconds,
  );
}
